import React from 'react'
import AdminInventoryManagerViewModel from 'admin/inventory/InventoryManagerViewModel'
import {
  ColumnId,
  InventoryColumnDetails,
} from 'admin/inventory/InventoryTableColumns'
import InventoryItem, {
  InventoryItemPricing,
} from 'admin/inventory/InventoryItem'
import { observer } from 'mobx-react'
import {
  CheckCell,
  DateCell,
  ImageCell,
  TableCell,
  TableRow,
} from 'components/Table'
import Icon from 'components/Icon'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import { ColumnDensity } from 'components/Table/TableCell'
import { keyBy, mapValues } from 'lodash'
import styles from './InventoryRow.sass'

const usDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

function formatDate(date?: number | Date | undefined) {
  const parts = usDateFormatter.formatToParts(date)
  const partsMap = mapValues(keyBy(parts, 'type'), 'value')

  return `${partsMap.day} ${partsMap.month} ${partsMap.year}`
}

interface InventoryRowProps {
  vm: AdminInventoryManagerViewModel
  inventoryItem: InventoryItem
}

export default observer(function InventoryRow({
  vm,
  inventoryItem,
}: InventoryRowProps) {
  const columns = vm.columns

  return (
    <TableRow data-test={`inventory-items-list-item-${inventoryItem.name}`}>
      {columns.map((column) => {
        switch (column.id) {
          case ColumnId.ServiceItem:
            return (
              <WorkItemCell
                name={inventoryItem.name}
                shortId={inventoryItem.shortId}
                override={inventoryItem.override}
                key={column.id}
              />
            )
          case ColumnId.Global:
            return (
              <CheckCell
                checked={inventoryItem.global}
                color="var(--blue400)"
                key={column.id}
              />
            )
          case ColumnId.Hidden:
            return (
              <CheckCell
                checked={inventoryItem.hidden}
                color="var(--blue400)"
                key={column.id}
              />
            )
          case ColumnId.LastUpdatedBy:
            return (
              <ImageCell
                title={inventoryItem.createdBy.displayName}
                image={inventoryItem.createdBy.avatar}
                imageFallback={inventoryItem.createdBy.avatarFallback}
                key={column.id}
              />
            )
          case ColumnId.ServiceType:
            return (
              <TableCell
                align={column.align}
                key={column.id}
                dataTest={inventoryItem.id + '-service-type'}
              >
                {inventoryItem.serviceTypeToString}
              </TableCell>
            )
          case ColumnId.Version:
            return (
              <TableCell
                dataTest="inventory-item-version"
                align={column.align}
                key={column.id}
              >
                {inventoryItem.version}
              </TableCell>
            )
          case ColumnId.VersionCreated:
            return (
              <DateCell
                date={inventoryItem.dateCreated}
                density={ColumnDensity.Compact}
                format={formatDate}
                dataTest="inventory-date-created"
                key={column.id}
              />
            )
          case ColumnId.Courtesy:
            return (
              <TableCell align={column.align} key={column.id}>
                {inventoryItem.courtesyAmount}
              </TableCell>
            )
          case ColumnId.PriceType:
            return (
              <TableCell
                align={column.align}
                key={column.id}
                dataTest={inventoryItem.id + '-price-type'}
              >
                {inventoryItem.priceTypeToString}
              </TableCell>
            )
          case ColumnId.SupportingItems:
            return (
              <TableCell
                align={column.align}
                key={column.id}
                dataTest={inventoryItem.id + '-supporting-items'}
              >
                {inventoryItem.supportingItemsCount}
              </TableCell>
            )
          case ColumnId.Price:
            return (
              <PriceCell
                price={inventoryItem.price}
                priceType={inventoryItem.priceType}
                column={column}
                key={column.id}
              />
            )
          case ColumnId.ProPrice:
            return (
              <PriceCell
                price={inventoryItem.proPrice}
                priceType={inventoryItem.priceType}
                column={column}
                key={column.id}
              />
            )
          case ColumnId.Actions:
            return (
              <TableCell
                className={styles.sticky}
                align={column.align}
                key={column.id}
              >
                <RowActions item={inventoryItem} vm={vm} />
              </TableCell>
            )
          default:
            return <TableCell key={column.id} />
        }
      })}
    </TableRow>
  )
})

interface WorkItemProps {
  name: string
  shortId: string
  override: boolean
}

const WorkItemCell = observer(({ name, shortId, override }: WorkItemProps) => {
  return (
    <TableCell>
      <div className={styles.workItem}>
        <span className={styles.name}>
          {name}
          {override && <span className={styles.override}> *</span>}
        </span>
        <span className={styles.description}>{shortId}</span>
      </div>
    </TableCell>
  )
})

interface PriceCellProps {
  price: number
  priceType: InventoryItemPricing
  column: InventoryColumnDetails
}

const PriceCell = observer(({ price, priceType, column }: PriceCellProps) => {
  if (
    priceType === InventoryItemPricing.Markup ||
    priceType === InventoryItemPricing.Markdown
  ) {
    return <TableCell align={column.align}>{price.toFixed(2)}%</TableCell>
  }

  return <TableCell align={column.align}>${price.toFixed(2)}</TableCell>
})

interface RowActionsProps {
  item: InventoryItem
  vm: AdminInventoryManagerViewModel
}

const RowActions = observer(({ item, vm }: RowActionsProps) => {
  if (item.global && item.archived && !vm.globalSelected) {
    return
  }

  return (
    <Dropdown
      dataTest={`inventory-item-actions-${item.name}`}
      alignContent="right"
      autoDismiss
      position="bottom-end"
      button={<Icon name="more" />}
    >
      <DropdownTitle>Actions</DropdownTitle>
      {((item.global && vm.globalSelected) || !item.global) && (
        <DropdownItem
          onClick={() => vm.editInventoryItemViewModel.open(item)}
          data-test={`inventory-item-actions-update-${item.name}`}
        >
          <div className={styles.rowActionItem}>
            <Icon size={24} name="edit-outlined" />
            <span className={styles.rowActionItemText}>Edit Item</span>
          </div>
        </DropdownItem>
      )}
      {item.global && !item.archived && vm.canManageWorkspaceInventory && (
        <DropdownItem
          onClick={() => vm.overrideInventoryItemViewModel.open(item)}
          data-test={`inventory-item-actions-override-${item.name}`}
        >
          <div className={styles.rowActionItem}>
            <Icon size={24} name="edit-outlined" />
            <span className={styles.rowActionItemText}>
              {vm.searcher.workspace
                ? 'Override item'
                : 'New workspace override'}
            </span>
          </div>
        </DropdownItem>
      )}

      {((item.global && vm.globalSelected) || !item.global) &&
        !item.archived && (
          <DropdownItem
            onClick={() => vm.archiveItemViewModel.open(item)}
            data-test={`inventory-item-actions-archive-${item.name}`}
          >
            <div className={styles.rowActionItem}>
              <Icon size={24} name="close" />
              <span className={styles.rowActionItemText}>
                Archive{item.override ? ' Override' : ' Item'}
              </span>
            </div>
          </DropdownItem>
        )}

      {((item.global && vm.globalSelected) || !item.global) &&
        item.archived && (
          <DropdownItem
            onClick={() => vm.unarchiveItemViewModel.open(item)}
            data-test={`inventory-item-actions-unarchive-${item.name}`}
          >
            <div className={styles.rowActionItem}>
              <Icon size={24} name="close" />
              <span className={styles.rowActionItemText}>
                Unarchive{item.override ? ' Override' : ' Item'}
              </span>
            </div>
          </DropdownItem>
        )}
    </Dropdown>
  )
})
